import { InjectionToken } from '@angular/core';

/**
 * A service that remembers something about the user who is signed in.
 *
 * Anything held in memory rather than in storage survives signing out, because
 * signing out is a route change and not a page load: the Angular application is
 * never torn down, so every root-provided service and everything it is holding
 * carries straight over into the next session in that tab. Storage is cleared on
 * the way out and the browser's own caches are addressed separately; this is the
 * layer in between, and it was being missed.
 *
 * That is how a department setting switched off by an administrator went on being
 * reported as switched on to the very person checking whether the change had
 * taken effect — signing out and back in did not shift it, and only a hard reload
 * did.
 *
 * Implement this on any service that keeps a per-user or per-session answer, and
 * register it against `SESSION_SCOPED_STATE`. The point of the token is that the
 * teardown does not have to know what exists: a service declares that it
 * remembers something, and the session boundary drops it. A hard-coded list in
 * the teardown is the arrangement that let this be forgotten in the first place,
 * and it drags a dependency on every feature into the core.
 */
export interface SessionScopedState {
  /**
   * Drop everything remembered for the session that is ending.
   *
   * Called on the way out and again on the way in, so a session that ended
   * without passing through the normal sign-out still starts clean. It has to be
   * safe to call at any time, including twice in a row and before anything has
   * been remembered at all.
   */
  clearSessionState(): void;
}

/**
 * Every registered holder of session-scoped state.
 *
 * Register with `multi: true`, pointing at the existing singleton rather than a
 * new instance — the state to drop is the one the rest of the app is reading:
 *
 *   { provide: SESSION_SCOPED_STATE, useExisting: MyService, multi: true }
 */
export const SESSION_SCOPED_STATE = new InjectionToken<
  readonly SessionScopedState[]
>('SESSION_SCOPED_STATE');
